// =============================================================================
// SIREPPAR - Consultas de partidos: bandeja de la SEP (#390)
// =============================================================================
// Bandeja única: todas las consultas de todos los partidos. La asignación solo
// marca quién se ocupa; no oculta consultas. "Mis consultas" es un filtro
// opcional, desactivado por defecto. El tipo de expediente y el tipo de partido
// son filtros más, en "Todos" de entrada.
// =============================================================================

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/24/outline';
import DataTable, { Column } from '../../components/common/DataTable';
import EstadoConsultaBadge from '../../components/common/EstadoConsultaBadge';
import PestaniasComunicaciones from '../../components/common/PestaniasComunicaciones';
import { useAuth } from '../../contexts/AuthContext';
import { consultaSepService, partidoService } from '../../services/api';
import { TIPO_TRAMITE_LABELS } from '../../utils/tiposExpediente';
import {
  ETIQUETA_ESTADO_CONSULTA,
  ETIQUETA_ORIGEN,
  EstadoConsulta,
  OrigenMensaje,
  formatearFecha,
  puedeGestionarConsultas,
} from '../../utils/consultas';

interface FilaConsultaSep {
  id: string;
  asunto: string;
  estado: EstadoConsulta;
  ultimoMensajeEn: string;
  origenUltimoMensaje: OrigenMensaje | null;
  partido: { id: string; denominacion: string; numero: number | null };
  asignadaA: { id: string; nombre: string; apellido: string } | null;
  partidoConIntimacionVigente: boolean;
  iniciadaPor: { nombre: string; apellido: string; origen: OrigenMensaje } | null;
}

interface PartidoOpcion {
  id: string;
  denominacion: string;
  numero: number | null;
}

/** Tipo de expediente: consultas generales (sin expediente) o cada tipo de trámite. */
const OPCIONES_TIPO_EXPEDIENTE: Array<{ valor: string; etiqueta: string }> = [
  { valor: 'SIN_EXPEDIENTE', etiqueta: 'Sin expediente' },
  ...Object.entries(TIPO_TRAMITE_LABELS).map(([valor, etiqueta]) => ({ valor, etiqueta })),
];

/** Tipo de partido (enum TipoPartido del backend). */
const OPCIONES_TIPO_PARTIDO: Array<{ valor: string; etiqueta: string }> = [
  { valor: 'MUNICIPAL', etiqueta: 'Municipal' },
  { valor: 'DISTRITO', etiqueta: 'Distrito' },
  { valor: 'PROVINCIAL', etiqueta: 'Provincial' },
  { valor: 'NACIONAL', etiqueta: 'Nacional' },
];

const FILTROS_INICIALES = {
  tipoExpediente: '',
  tipoPartido: '',
  estado: '',
  partidoId: '',
  fechaDesde: '',
  fechaHasta: '',
  conIntimacionVigente: false,
  mias: false,
};

export default function GestionConsultasPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const cambiar = <K extends keyof typeof FILTROS_INICIALES>(campo: K, valor: (typeof FILTROS_INICIALES)[K]) => {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    setPage(1);
  };

  const params: Record<string, string> = {
    page: String(page),
    pageSize: String(pageSize),
    ...(filtros.tipoExpediente ? { tipoExpediente: filtros.tipoExpediente } : {}),
    ...(filtros.tipoPartido ? { tipoPartido: filtros.tipoPartido } : {}),
    ...(filtros.estado ? { estado: filtros.estado } : {}),
    ...(filtros.partidoId ? { partidoId: filtros.partidoId } : {}),
    ...(filtros.fechaDesde ? { fechaDesde: filtros.fechaDesde } : {}),
    ...(filtros.fechaHasta ? { fechaHasta: filtros.fechaHasta } : {}),
    ...(filtros.conIntimacionVigente ? { conIntimacionVigente: 'true' } : {}),
    ...(filtros.mias ? { mias: 'true' } : {}),
  };

  const { data, isLoading } = useQuery({
    queryKey: ['sep-consultas', params],
    queryFn: () => consultaSepService.list(params),
  });

  const { data: partidos = [] } = useQuery<PartidoOpcion[]>({
    queryKey: ['sep-consultas-partidos'],
    queryFn: async () => {
      const res = await partidoService.list({ pageSize: 500 });
      return res.data?.data?.items ?? res.data?.data ?? [];
    },
  });

  const encabezado = (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Consultas de partidos</h1>
        <p className="text-sm text-gray-500 mt-1">Consultas entre los partidos y alianzas y la Secretaría</p>
      </div>
      {puedeGestionarConsultas(user?.rol) && (
        <button
          onClick={() => navigate('/gestion-consultas/nueva')}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          <PlusIcon className="w-4 h-4" />
          Nueva consulta
        </button>
      )}
    </div>
  );

  const filas: FilaConsultaSep[] = data?.data ?? [];
  const total: number = data?.meta?.totalItems ?? 0;

  const columns: Column<FilaConsultaSep>[] = [
    {
      key: 'partido',
      header: 'Partido',
      render: (_v, fila) => (
        <div>
          <span className="text-gray-900">
            {fila.partido.numero != null ? `${fila.partido.numero} — ` : ''}{fila.partido.denominacion}
          </span>
          {fila.partidoConIntimacionVigente && (
            <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              Intimación vigente
            </span>
          )}
        </div>
      ),
    },
    { key: 'asunto', header: 'Asunto', render: (valor) => <span className="font-medium text-gray-900">{valor}</span> },
    { key: 'estado', header: 'Estado', render: (_v, fila) => <EstadoConsultaBadge estado={fila.estado} /> },
    { key: 'ultimoMensajeEn', header: 'Último mensaje', render: (_v, fila) => formatearFecha(fila.ultimoMensajeEn) },
    {
      key: 'origenUltimoMensaje',
      header: 'Último mensaje de',
      render: (_v, fila) => (fila.origenUltimoMensaje ? ETIQUETA_ORIGEN[fila.origenUltimoMensaje] : '—'),
    },
    {
      key: 'asignadaA',
      header: 'Asignada a',
      render: (_v, fila) =>
        fila.asignadaA ? `${fila.asignadaA.nombre} ${fila.asignadaA.apellido}` : <span className="text-gray-400">Sin asignar</span>,
    },
    {
      key: 'iniciadaPor',
      header: 'Iniciada por',
      render: (_v, fila) =>
        fila.iniciadaPor ? (
          <span>
            {fila.iniciadaPor.nombre} {fila.iniciadaPor.apellido}
            <span className="text-gray-500"> ({ETIQUETA_ORIGEN[fila.iniciadaPor.origen]})</span>
          </span>
        ) : '—',
    },
  ];

  return (
    <div>
      <PestaniasComunicaciones />
      {encabezado}

      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-4">
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Tipo de expediente
          <select value={filtros.tipoExpediente} onChange={(e) => cambiar('tipoExpediente', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm max-w-xs">
            <option value="">Todos</option>
            {OPCIONES_TIPO_EXPEDIENTE.map((o) => (
              <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Tipo de partido
          <select value={filtros.tipoPartido} onChange={(e) => cambiar('tipoPartido', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
            <option value="">Todos</option>
            {OPCIONES_TIPO_PARTIDO.map((o) => (
              <option key={o.valor} value={o.valor}>{o.etiqueta}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Estado
          <select value={filtros.estado} onChange={(e) => cambiar('estado', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm">
            <option value="">Todos</option>
            {(Object.keys(ETIQUETA_ESTADO_CONSULTA) as EstadoConsulta[]).map((e) => (
              <option key={e} value={e}>{ETIQUETA_ESTADO_CONSULTA[e]}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Partido
          <select value={filtros.partidoId} onChange={(e) => cambiar('partidoId', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm max-w-xs">
            <option value="">Todos</option>
            {partidos.map((p) => (
              <option key={p.id} value={p.id}>{p.numero != null ? `${p.numero} — ` : ''}{p.denominacion}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Desde
          <input type="date" value={filtros.fechaDesde} onChange={(e) => cambiar('fechaDesde', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
        </label>
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          Hasta
          <input type="date" value={filtros.fechaHasta} onChange={(e) => cambiar('fechaHasta', e.target.value)} className="border border-gray-300 rounded-md px-3 py-1.5 text-sm" />
        </label>
        <label className="text-sm text-gray-700 flex items-center gap-2 py-1.5">
          <input type="checkbox" checked={filtros.conIntimacionVigente} onChange={(e) => cambiar('conIntimacionVigente', e.target.checked)} />
          Solo partidos con intimación vigente
        </label>
        <label className="text-sm text-gray-700 flex items-center gap-2 py-1.5">
          <input type="checkbox" checked={filtros.mias} onChange={(e) => cambiar('mias', e.target.checked)} />
          Mis consultas
        </label>
      </div>

      <DataTable
        data={filas}
        columns={columns}
        loading={isLoading}
        emptyMessage="No hay consultas con estos filtros."
        onRowClick={(fila) => navigate(`/gestion-consultas/${fila.id}`)}
        pagination={{
          page,
          pageSize,
          total,
          onPageChange: setPage,
          onPageSizeChange: (n) => { setPageSize(n); setPage(1); },
        }}
      />
    </div>
  );
}
